'use client'
import { useRouter } from 'next/navigation'
import React, { useState } from 'react'
import { toast } from 'sonner'

import { cn } from '@/lib/utils'
import { getLocalizedTranslationData } from '@/utilities/translationsData'
import { useLocale } from '../LocaleContext'
import { BrowseProductsButton } from './BrowseProductsButton'
import { DownloadECatalogButton } from './DownloadECatalogButton'

function IconSearch({
  className,
  inline,
  ...props
}: React.SVGProps<SVGSVGElement> & { inline?: boolean }) {
  return (
    <svg
      className={`size-5 shrink-0 text-gallium-cta-panel sm:size-[22px] ${!inline ? 'absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none sm:right-4' : ''} ${className ?? ''}`.trim()}
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </svg>
  )
}

export type SearchAndActionProps = {
  catalogueUrl?: string | null
  catalogueFilename?: string
}

export function SearchAndAction({
  catalogueUrl,
  catalogueFilename = 'E-Catalogue.pdf',
}: SearchAndActionProps) {
  const router = useRouter()
  const [keyword, setKeyword] = useState('')
  const { locale, translatedData } = useLocale()
  const staticCommon = getLocalizedTranslationData((locale as AppLocale) ?? 'en').common

  const handleSearch = () => {
    const trimmed = keyword.trim()
    if (!trimmed || trimmed.length < 4) {
      toast.dismiss()
      toast.warning(
        staticCommon?.search_length_validation ??
          translatedData?.common?.search_length_validation ??
          'Please enter at least 4 characters for search.',
      )
      return
    }
    router.push(`/products/search?q=${encodeURIComponent(trimmed)}`)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    toast.dismiss()
    handleSearch()
  }

  return (
    <section
      className={[
        'gallium-search-action relative z-10 isolate box-border bg-[#E40520] opacity-100 mb-14',
        'w-full min-w-0 max-w-[100vw] overflow-x-hidden sticky top-0',
        'max-[899px]:max-w-[calc(100vw-2rem)] max-[899px]:mx-auto',
        'max-[899px]:min-h-[145px] max-[899px]:overflow-y-visible',
        'min-[640px]:max-[899px]:min-h-[180px]',
        'max-[639px]:max-[899px]:mt-5 max-[639px]:max-[899px]:pt-4 max-[639px]:max-[899px]:pb-3',
        'min-[640px]:max-[899px]:mt-5 min-[640px]:max-[899px]:pt-5 min-[640px]:max-[899px]:pb-4',
        'max-[400px]:max-[899px]:pl-[max(0.75rem,env(safe-area-inset-left))] max-[400px]:max-[899px]:pr-[max(0.75rem,env(safe-area-inset-right))]',
        'min-[400px]:max-[899px]:pl-[max(1rem,env(safe-area-inset-left))] min-[400px]:max-[899px]:pr-[max(1rem,env(safe-area-inset-right))]',
        'min-[900px]:overflow-visible',
        'min-[900px]:min-h-[300px] min-[900px]:mt-8',
        'pt-5 pb-[max(1rem,env(safe-area-inset-bottom))] pl-[max(0.75rem,env(safe-area-inset-left))] pr-[max(0.75rem,env(safe-area-inset-right))]',
        'sm:pt-6 sm:pb-5 sm:pl-[max(1rem,env(safe-area-inset-left))] sm:pr-[max(1rem,env(safe-area-inset-right))]',
        'min-[900px]:mx-auto min-[900px]:mt-8',
        'min-[900px]:w-[1386px] min-[900px]:max-w-[calc(100vw-2rem)] min-[900px]:min-h-[300px] min-[900px]:max-h-none min-[900px]:h-[321px]',
        'min-[900px]:pt-[45px] min-[900px]:pb-[20px] min-[900px]:pl-[max(70px,env(safe-area-inset-left))] min-[900px]:pr-[max(70px,env(safe-area-inset-right))]',
      ].join(' ')}
      style={{
        backgroundImage: 'url(/images/SearchBG.svg)',
        backgroundSize: 'stretch',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
      aria-labelledby="search-heading"
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
    >
      <div
        className={[
          'mx-auto w-full min-w-0 max-w-[1200px] px-0',
          'flex flex-col items-stretch max-[639px]:gap-2 min-[640px]:max-[899px]:gap-3 max-[899px]:pt-0 max-[899px]:mt-0',
          'pt-2 mt-4 min-[900px]:gap-0',
          'min-[1200px]:max-w-[1386px]',
        ].join(' ')}
      >
        {/* Div 1: SEARCH heading only */}
        <div className="min-w-0 w-full max-w-full mt-0">
          <h2
            id="search-heading"
            className={[
              'font-bebas-neue font-bold uppercase text-gallium-white break-words',
              'text-[28px] leading-tight max-[599px]:mb-1.5 max-[899px]:ml-0',
              'min-[600px]:max-[899px]:text-[32px] min-[600px]:max-[899px]:mb-2',
              'min-[900px]:text-[61.6px] min-[900px]:leading-[68px] min-[900px]:mb-5 min-[900px]:ml-0',
              'max-w-[617px] box-border',
            ].join(' ')}
          >
            {translatedData?.common?.search ?? 'Search'}
          </h2>
        </div>

        {/* Subtitle + input + buttons */}
        <div className="min-w-0 w-full max-w-full flex flex-col max-[639px]:gap-2 min-[640px]:max-[899px]:gap-3">
          <div
            className={[
              'flex flex-col items-stretch gap-3 min-w-0',
              'min-[900px]:flex-row min-[900px]:items-end min-[900px]:gap-[40px]',
              'min-[1440px]:flex-nowrap gallium-search-action__row gallium-search-action__row--narrow',
              'min-[1200px]:gap-[40px]',
            ].join(' ')}
          >
            <div className="relative w-full min-w-0 max-w-full flex-1 min-[900px]:min-w-0">
              <label htmlFor="gallium-search" className="visually-hidden">
                {translatedData?.common.search_hint ?? 'Search by reference, name, or brand'}
              </label>
              <p
                className={[
                  'font-poppins font-normal text-gallium-white break-words max-w-[617px] box-border',
                  'text-sm leading-[1.5] max-[899px]:mb-1.5',
                  'min-[400px]:text-base',
                  'min-[900px]:mb-0 min-[900px]:text-[20px] min-[900px]:leading-7',
                ].join(' ')}
              >
                {translatedData?.common.search_prompt ??
                  'Looking for a filter, an application, an accessory?'}
              </p>
              <form
                className="relative w-full min-w-0 max-w-full"
                style={{ maxWidth: 617 }}
                onSubmit={handleSubmit}
              >
                <input
                  id="gallium-search"
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.code === 'Enter') handleSearch()
                  }}
                  className={[
                    'w-full min-w-0 max-w-full box-border touch-manipulation',
                    'rounded max-[899px]:h-12 max-[899px]:min-h-12 max-[899px]:py-2.5',
                    'text-sm text-gallium-dark bg-gallium-white',
                    locale == 'ar' ? 'pr-3 pl-10  min-[400px]:pl-11 min-[900px]:pl-12' : 'pl-3 pr-10 min-[400px]:pr-11 min-[900px]:pr-12',
                    'border-0 placeholder:text-gallium-placeholder focus:outline-none [font-family:inherit]',
                    'min-[400px]:text-base',
                    'min-[900px]:h-[71.24px] min-[900px]:min-h-[71.24px] min-[900px]:py-3 min-[900px]:rounded',
                  ].join(' ')}
                  placeholder={
                    translatedData?.common.search_placeholder ?? 'Reference, Part Number...'
                  }
                  autoComplete="off"
                  aria-describedby="search-desc"
                  aria-label={translatedData?.common.search_hint ?? 'Search by part number'}
                />
                <button
                  type="submit"
                  disabled={!keyword.trim()}
                  className={cn(
                    'absolute top-1/2 -translate-y-1/2 p-0 border-0 bg-transparent cursor-pointer text-gallium-cta-panel hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-gallium-white focus:ring-offset-2 focus:ring-offset-[#E40520] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:opacity-50',
                    locale === 'ar' ? 'left-3 sm:left-4' : 'right-3 sm:right-4',
                  )}
                  aria-label={translatedData?.common.search ?? 'Search'}
                  aria-disabled={!keyword.trim()}
                >
                  <IconSearch inline />
                </button>
              </form>
            </div>
            <div
              className={[
                'hidden min-[1440px]:flex min-[1440px]:flex-shrink-0 min-[1440px]:items-end min-[1440px]:gap-[30px]',
                'min-[1440px]:flex-nowrap min-[1440px]:max-w-full',
                'min-[1200px]:gap-[30px] min-w-0',
              ].join(' ')}
            >
              <BrowseProductsButton href="/products" />
              <DownloadECatalogButton href={catalogueUrl} download={catalogueFilename} />
            </div>
          </div>
        </div>
        <p id="search-desc" className="visually-hidden">
          {translatedData?.common.search_desc ??
            'Enter a product reference, name, or brand to search.'}
        </p>
      </div>
    </section>
  )
}
